/**
 * Mock router con persistencia.
 *
 * Cada handler lee/escribe contra `localStorage` vía `./store`. Las mutaciones
 * persisten entre reloads — el demo se siente como un admin de verdad.
 *
 * Patrón:
 *   GET  /api/v1/products       → readCollection('products')
 *   POST /api/v1/products       → insertOne('products', {...body})  → genera id
 *   PATCH /api/v1/products/:id  → updateOne('products', id, body)
 *   DELETE /api/v1/products/:id → removeOne('products', id)
 *
 * Default: GET no matcheado → `{ data: [] }`. Mutación no matcheada → `{ success: true }`.
 */
import {
  readCollection, writeCollection, readSingleton, writeSingleton,
  findById, insertOne, updateOne, removeOne, genId,
} from './store'
import { bootstrapDemoStore } from './bootstrap'
import { LOYALTY_STATS_DEMO } from './seeds/loyalty'
import {
  ANALYTICS_DEMO, ANALYTICS_OVERVIEW_DEMO, TENANT_USAGE_DEMO, ANALYTICS_SNIPPET_DEMO,
  TENANT_ANALYTICS_DEMO,
} from './seeds/analytics'
import { SHIPPING_DEMO } from './seeds/shipping'

bootstrapDemoStore()

interface RouterRequest {
  path: string
  method: string
  body?: unknown
}

type Handler = (params: string[], req: RouterRequest) => unknown

interface Route {
  pattern: RegExp
  method: string
  handler: Handler
}

const routes: Route[] = []

function on(method: string, pattern: RegExp, handler: Handler) {
  routes.push({ pattern, method, handler })
}

const body = (req: RouterRequest): Record<string, unknown> =>
  (req.body as Record<string, unknown>) ?? {}

// ============================================================
// Admin / plataforma
// ============================================================

on('GET', /^\/api\/admin\/me$/, () => ({ data: readSingleton('me') }))

on('GET', /^\/api\/admin\/dashboard$/, () => ({
  data: {
    tenants: { total: 1, active: 1 },
    catalog: {
      totalProducts: readCollection('products').length,
      totalCustomers: readCollection('customers').length,
    },
    commerce: {
      ordersThisMonth: readCollection('orders').length,
      mrr: 18_640_000,
    },
    usage: { period: '2026-05', totalApiRequests: 124_500, totalBandwidthGB: 12.4, totalStorageGB: 2.8 },
    system: { healthy: true, checkedAt: new Date().toISOString() },
  },
}))

on('GET', /^\/api\/admin\/tenants$/, () => ({ data: [readSingleton('tenant')] }))
on('GET', /^\/api\/admin\/tenants\/([^/]+)$/, () => ({ data: readSingleton('tenant') }))
on('PATCH', /^\/api\/admin\/tenants\/([^/]+)$/, (_, req) => {
  const current = readSingleton<Record<string, unknown>>('tenant') ?? {}
  const next = { ...current, ...body(req) }
  writeSingleton('tenant', next)
  return { data: next }
})

on('GET', /^\/api\/admin\/api-keys\/tenant\/([^/]+)$/, () => ({ data: readCollection('apiKeys') }))
on('POST', /^\/api\/admin\/api-keys/, (_, req) => {
  const created = insertOne('apiKeys', {
    id: genId('key'),
    name: 'New API key',
    prefix: 'sk_live_DEMO',
    environment: 'live',
    scopes: [],
    lastUsedAt: null,
    createdAt: new Date().toISOString(),
    revokedAt: null,
    ...body(req),
  })
  return { data: { ...created, fullKey: 'sk_live_DEMO_FAKE_NEVER_REAL' } }
})
on('DELETE', /^\/api\/admin\/api-keys\/([^/]+)$/, (params) => {
  removeOne('apiKeys', params[0])
  return { success: true }
})

on('GET', /^\/api\/admin\/audit/, () => ({ data: readCollection('auditLog') }))

// Uso del tenant (consumo del plan: API requests, orders, bandwidth, storage).
on('GET', /^\/api\/admin\/tenants\/([^/]+)\/usage$/, () => ({ data: TENANT_USAGE_DEMO }))

// Dashboard del store: KPIs + gráficos + popular products (matchea IDs reales del seed).
on('GET', /^\/api\/admin\/tenants\/([^/]+)\/analytics(\?.*)?$/, (_, req) => {
  const url = new URL(`http://x${req.path}`)
  const period = (url.searchParams.get('period') as 'day' | 'week' | 'month' | 'year' | null) ?? 'week'
  return { data: TENANT_ANALYTICS_DEMO.forPeriod(period) }
})

// ============================================================
// Products
// ============================================================

on('GET', /^\/api\/v1\/products$/, () => {
  const data = readCollection('products')
  return { data, total: data.length }
})
on('GET', /^\/api\/v1\/products\/([^/]+)$/, (params) => {
  const found = findById('products', params[0])
  return { data: found ?? readCollection('products')[0] }
})
on('POST', /^\/api\/v1\/products$/, (_, req) => {
  const data = body(req)
  const created = insertOne('products', {
    id: genId('prd'),
    sku: data.sku ?? 'NEW-SKU',
    name: data.name ?? 'Nuevo producto',
    brand: data.brand ?? '',
    description: data.description ?? '',
    price: data.price ?? 0,
    compareAtPrice: data.compareAtPrice ?? null,
    taxRate: data.taxRate ?? 0,
    status: data.status ?? 'draft',
    images: data.images ?? [],
    categories: data.categories ?? [],
    variants: data.variants ?? [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...data,
  })
  return { data: created }
})
on('PATCH', /^\/api\/v1\/products\/([^/]+)$/, (params, req) => {
  const updated = updateOne('products', params[0], { ...body(req), updatedAt: new Date().toISOString() } as Record<string, unknown>)
  return { data: updated }
})
on('DELETE', /^\/api\/v1\/products\/([^/]+)$/, (params) => {
  removeOne('products', params[0])
  return { success: true }
})

// ============================================================
// Categories / product types
// ============================================================

on('GET', /^\/api\/v1\/categories/, () => ({ data: readCollection('categories') }))
on('POST', /^\/api\/v1\/categories/, (_, req) => {
  const created = insertOne('categories', {
    id: genId('cat'),
    productCount: 0,
    parentId: null,
    sortOrder: readCollection('categories').length + 1,
    isActive: true,
    ...body(req),
  } as Record<string, unknown> & { id: string })
  return { data: created }
})
on('PATCH', /^\/api\/v1\/categories\/([^/]+)$/, (params, req) => ({
  data: updateOne('categories', params[0], body(req) as never),
}))
on('DELETE', /^\/api\/v1\/categories\/([^/]+)$/, (params) => {
  removeOne('categories', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/product-types/, () => ({ data: readCollection('productTypes') }))
on('POST', /^\/api\/v1\/product-types/, (_, req) => ({
  data: insertOne('productTypes', { id: genId('pt'), ...body(req) } as never),
}))
on('PATCH', /^\/api\/v1\/product-types\/([^/]+)$/, (params, req) => ({
  data: updateOne('productTypes', params[0], body(req) as never),
}))
on('DELETE', /^\/api\/v1\/product-types\/([^/]+)$/, (params) => {
  removeOne('productTypes', params[0])
  return { success: true }
})

// ============================================================
// Inventory
// ============================================================

on('GET', /^\/api\/v1\/inventory/, () => ({ data: readCollection('inventory') }))
on('PATCH', /^\/api\/v1\/inventory$/, (_, req) => {
  // El backend acepta { updates: [{ inventoryId, quantity }] }.
  const data = body(req)
  const updates = (data.updates as Array<{ inventoryId: string; quantity?: number; lowStockThreshold?: number }>) ?? []
  for (const u of updates) {
    const patch: Record<string, unknown> = { updatedAt: new Date().toISOString() }
    if (u.quantity !== undefined) patch.quantity = u.quantity
    if (u.lowStockThreshold !== undefined) patch.lowStockThreshold = u.lowStockThreshold
    updateOne('inventory', u.inventoryId, patch as never)
  }
  return { success: true }
})
on('PATCH', /^\/api\/v1\/inventory\/([^/]+)$/, (params, req) => ({
  data: updateOne('inventory', params[0], { ...body(req), updatedAt: new Date().toISOString() } as never),
}))

// ============================================================
// Orders
// ============================================================

on('GET', /^\/api\/v1\/orders$/, () => {
  const data = readCollection('orders')
  return { data, total: data.length }
})
on('GET', /^\/api\/v1\/orders\/([^/]+)$/, (params) => {
  const order = findById<Record<string, unknown>>('orders', params[0])
  if (!order) return { data: null }
  // Items + transactions: enriquecemos el detalle con un snapshot sintético.
  const products = readCollection<{ id: string; name: string; sku: string; description?: string; images?: string[]; variants?: { id: string; name: string; sku: string; attributes: Record<string, string> }[] }>('products')
  const fallbackProduct = products[0]
  return {
    data: {
      ...order,
      items: [
        {
          id: 'oi_demo',
          productId: fallbackProduct?.id,
          variantId: fallbackProduct?.variants?.[0]?.id,
          quantity: 1,
          price: order.total ?? '0',
          taxRate: '0',
          taxAmount: '0',
          productSnapshot: {
            productId: fallbackProduct?.id,
            name: fallbackProduct?.name,
            description: fallbackProduct?.description,
            sku: fallbackProduct?.sku,
            images: fallbackProduct?.images,
            variant: fallbackProduct?.variants?.[0],
          },
        },
      ],
      transactions: readCollection<Record<string, unknown>>('payments').filter((p) => p.orderId === order.id),
    },
  }
})
on('PATCH', /^\/api\/v1\/orders\/([^/]+)$/, (params, req) => ({
  data: updateOne('orders', params[0], { ...body(req), updatedAt: new Date().toISOString() } as never),
}))

// ============================================================
// Customers
// ============================================================

on('GET', /^\/api\/v1\/customers$/, () => {
  const data = readCollection<{ totalSpent: number; ordersCount: number; paidOrdersCount: number; cancelledCount: number; isActive: boolean; createdAt: string }>('customers')
  const totalCustomers = data.length
  // "Nuevos" = creados en los últimos 30 días
  const since = Date.now() - 30 * 24 * 60 * 60 * 1000
  const newCustomers = data.filter((c) => new Date(c.createdAt).getTime() >= since).length
  const repeatBuyers = data.filter((c) => c.paidOrdersCount > 1).length
  const returnRate = totalCustomers > 0 ? (repeatBuyers / totalCustomers) * 100 : 0
  const totalRevenue = data.reduce((s, c) => s + (c.totalSpent || 0), 0)
  const totalOrders = data.reduce((s, c) => s + (c.paidOrdersCount || 0), 0)
  const avgOrderRevenue = totalOrders > 0 ? totalRevenue / totalOrders : 0
  const refunds = data.reduce((s, c) => s + (c.cancelledCount || 0), 0)
  return {
    data,
    stats: {
      totalCustomers,
      totalCustomersDeltaPct: 12.4,
      newCustomers,
      newCustomersDeltaPct: 8.2,
      returnCustomerRate: returnRate,
      returnCustomerRateDeltaPct: 3.1,
      avgOrderRevenue,
      avgOrderRevenueDeltaPct: -2.7,
      refunds,
      refundsDeltaPct: -15.0,
    },
  }
})
on('GET', /^\/api\/v1\/customers\/([^/]+)$/, (params) => {
  const customer = findById<Record<string, unknown>>('customers', params[0])
  return {
    data: customer
      ? { ...customer, orders: readCollection<Record<string, unknown>>('orders').slice(0, 3) }
      : null,
  }
})

// ============================================================
// Coupons
// ============================================================

on('GET', /^\/api\/v1\/coupons/, () => ({ data: readCollection('coupons') }))
on('POST', /^\/api\/v1\/coupons/, (_, req) => ({
  data: insertOne('coupons', {
    id: genId('cpn'),
    isActive: true,
    usedCount: 0,
    expiresAt: null,
    createdAt: new Date().toISOString(),
    ...body(req),
  } as never),
}))
on('PATCH', /^\/api\/v1\/coupons\/([^/]+)$/, (params, req) => ({
  data: updateOne('coupons', params[0], body(req) as never),
}))
on('DELETE', /^\/api\/v1\/coupons\/([^/]+)$/, (params) => {
  removeOne('coupons', params[0])
  return { success: true }
})

// ============================================================
// Loyalty
// ============================================================

on('GET', /^\/api\/v1\/loyalty\/tiers/, () => ({ data: readCollection('loyaltyTiers') }))
on('POST', /^\/api\/v1\/loyalty\/tiers/, (_, req) => ({
  data: insertOne('loyaltyTiers', {
    id: genId('tier'),
    sortOrder: readCollection('loyaltyTiers').length + 1,
    isActive: true,
    benefits: [],
    ...body(req),
  } as never),
}))
on('PATCH', /^\/api\/v1\/loyalty\/tiers\/([^/]+)$/, (params, req) => ({
  data: updateOne('loyaltyTiers', params[0], body(req) as never),
}))
on('DELETE', /^\/api\/v1\/loyalty\/tiers\/([^/]+)$/, (params) => {
  removeOne('loyaltyTiers', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/loyalty\/codes/, () => ({ data: readCollection('loyaltyCodes') }))
on('POST', /^\/api\/v1\/loyalty\/codes/, (_, req) => {
  const reqBody = body(req)
  const count = (reqBody.count as number) ?? 1
  const tierId = reqBody.tierId as string
  const tiers = readCollection<{ id: string; name: string }>('loyaltyTiers')
  const tier = tiers.find((t) => t.id === tierId) ?? tiers[0]
  const created = []
  for (let i = 0; i < count; i++) {
    const row = {
      id: genId('lc'),
      code: `${tier?.name?.slice(0, 3).toUpperCase() ?? 'TIE'}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
      tierId: tier?.id ?? tierId,
      tierName: tier?.name ?? 'Tier',
      batchId: genId('batch'),
      expiresAt: (reqBody.expiresAt as string) ?? null,
      redeemedByCustomerId: null,
      redeemedByEmail: null,
      redeemedAt: null,
      createdAt: new Date().toISOString(),
      status: 'active',
    }
    insertOne('loyaltyCodes', row as never)
    created.push(row)
  }
  return { data: created }
})
on('DELETE', /^\/api\/v1\/loyalty\/codes\/([^/]+)$/, (params) => {
  removeOne('loyaltyCodes', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/loyalty\/members/, () => ({ data: readCollection('loyaltyMembers') }))
on('DELETE', /^\/api\/v1\/loyalty\/members\/([^/]+)$/, (params) => {
  removeOne('loyaltyMembers', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/loyalty\/stats/, () => ({
  data: {
    activeTiers: readCollection<{ isActive: boolean }>('loyaltyTiers').filter((t) => t.isActive).length,
    activeMembers: readCollection<{ isActive: boolean }>('loyaltyMembers').filter((m) => m.isActive).length,
    totalCodes: readCollection('loyaltyCodes').length,
    redeemedCodes: readCollection<{ status: string }>('loyaltyCodes').filter((c) => c.status === 'redeemed').length,
    ...LOYALTY_STATS_DEMO, // fallback si la UI espera más campos
  },
}))

// ============================================================
// Blog / comments / reviews / returns
// ============================================================

on('GET', /^\/api\/v1\/blog\/posts$/, () => ({ data: readCollection('blogPosts') }))
on('GET', /^\/api\/v1\/blog\/posts\/([^/]+)$/, (params) => ({
  data: findById('blogPosts', params[0]) ?? readCollection('blogPosts')[0],
}))
on('POST', /^\/api\/v1\/blog\/posts/, (_, req) => ({
  data: insertOne('blogPosts', {
    id: genId('pst'),
    status: 'draft',
    publishedAt: null,
    blocks: [],
    seo: { title: '', description: '' },
    ...body(req),
  } as never),
}))
on('PATCH', /^\/api\/v1\/blog\/posts\/([^/]+)$/, (params, req) => ({
  data: updateOne('blogPosts', params[0], body(req) as never),
}))
on('DELETE', /^\/api\/v1\/blog\/posts\/([^/]+)$/, (params) => {
  removeOne('blogPosts', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/comments/, () => ({ data: readCollection('comments') }))
on('PATCH', /^\/api\/v1\/comments\/([^/]+)$/, (params, req) => ({
  data: updateOne('comments', params[0], body(req) as never),
}))
on('DELETE', /^\/api\/v1\/comments\/([^/]+)$/, (params) => {
  removeOne('comments', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/reviews/, () => {
  const data = readCollection<{ status: string; rating: number }>('reviews')
  const total = data.length
  const pending = data.filter((r) => r.status === 'pending').length
  const approved = data.filter((r) => r.status === 'approved').length
  const rejected = data.filter((r) => r.status === 'rejected').length
  const avgRating = total > 0 ? data.reduce((s, r) => s + (r.rating || 0), 0) / total : 0
  return { data, stats: { total, pending, approved, rejected, avgRating } }
})
on('PATCH', /^\/api\/v1\/reviews\/([^/]+)$/, (params, req) => ({
  data: updateOne('reviews', params[0], body(req) as never),
}))
on('DELETE', /^\/api\/v1\/reviews\/([^/]+)$/, (params) => {
  removeOne('reviews', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/returns/, () => ({ data: readCollection('returns') }))
on('PATCH', /^\/api\/v1\/returns\/([^/]+)$/, (params, req) => ({
  data: updateOne('returns', params[0], body(req) as never),
}))

// ============================================================
// Analytics / payments / shipping (mostly read-only)
// ============================================================

// Específicos antes que el genérico (el router matchea en orden de declaración).
on('GET', /^\/api\/v1\/analytics\/overview/, () => ({ data: ANALYTICS_OVERVIEW_DEMO }))
on('GET', /^\/api\/v1\/analytics\/snippet/, () => ({ data: ANALYTICS_SNIPPET_DEMO }))
on('GET', /^\/api\/v1\/analytics/, () => ({ data: ANALYTICS_DEMO }))
on('GET', /^\/api\/v1\/reports/, () => ({ data: ANALYTICS_DEMO }))
on('GET', /^\/api\/v1\/payments/, () => ({ data: readCollection('payments') }))
on('GET', /^\/api\/v1\/transactions/, () => ({ data: readCollection('payments') }))
on('GET', /^\/api\/v1\/shipping/, () => ({ data: SHIPPING_DEMO }))

// ============================================================
// Email templates / settings / plugins / marketing / affiliates
// ============================================================

on('GET', /^\/api\/v1\/email-templates$/, () => ({ data: readCollection('emailTemplates') }))
// Persistir la selección de variant cambia el campo `selectedVariant` del kind correspondiente.
on('PATCH', /^\/api\/v1\/email-templates\/selection$/, (_, req) => {
  const data = body(req) as { kind?: string; variant?: string }
  if (!data.kind || !data.variant) return { success: false }
  const kinds = readCollection<{ kind: string; selectedVariant: string }>('emailTemplates')
  const next = kinds.map((k) => (k.kind === data.kind ? { ...k, selectedVariant: data.variant! } : k))
  writeCollection('emailTemplates', next)
  return { success: true }
})

on('GET', /^\/api\/v1\/settings/, () => ({ data: readCollection('settings') }))
on('POST', /^\/api\/v1\/settings/, (_, req) => ({
  data: insertOne('settings', { id: genId('s'), ...body(req) } as never),
}))
on('PATCH', /^\/api\/v1\/settings\/([^/]+)$/, (params, req) => {
  // El admin a veces busca por `key`; soportamos ambos.
  const settings = readCollection<{ id: string; key: string }>('settings')
  const target = settings.find((s) => s.id === params[0] || s.key === params[0])
  if (!target) return { success: false }
  return { data: updateOne('settings', target.id, body(req) as never) }
})
on('DELETE', /^\/api\/v1\/settings\/([^/]+)$/, (params) => {
  removeOne('settings', params[0])
  return { success: true }
})

on('GET', /^\/api\/v1\/plugins/, () => ({ data: [] }))
on('GET', /^\/api\/v1\/marketing/, () => ({ data: { subscribers: [], campaigns: [] } }))
on('GET', /^\/api\/v1\/affiliates/, () => ({ data: [] }))

// ============================================================
// Router
// ============================================================

export function mockRouter(req: RouterRequest): unknown {
  // Strip query string para que los patrones `^...$` matcheen aunque haya `?...`.
  // El handler recibe el req original (con `?...`) por si necesita los params.
  const idx = req.path.indexOf('?')
  const pathOnly = idx >= 0 ? req.path.slice(0, idx) : req.path
  for (const route of routes) {
    if (route.method !== req.method) continue
    const match = route.pattern.exec(pathOnly)
    if (match) {
      try {
        return route.handler(match.slice(1), req)
      } catch (err) {
        console.error('[mock-router] handler threw:', req.path, err)
        return { success: false, error: { message: 'mock handler error' } }
      }
    }
  }
  if (req.method === 'GET') return { data: [] }
  return { success: true }
}

export function mockRouterRaw(req: { path: string; method: string }): string {
  if (req.path.includes('.csv') || req.path.includes('export')) {
    return 'id,name,value\n'
  }
  return JSON.stringify(mockRouter(req as RouterRequest))
}
